import {
  ApiError,
  RequestAbortError,
  type CompanionPrefs,
  type CompanionTask,
  type CompanionToday,
} from '../../api/client.js';
import type { SessionController } from '../../session/controller.js';
import { accountEvents, type AccountEvents } from '../account-events.js';
import { confirmModal, h, toast, type View } from '../components/dom.js';
import { setConversationIntent } from '../conversation-intent.js';
import { faceMark } from '../components/face-mark.js';
import { icon } from '../components/icons.js';
import { revealForm } from '../components/reveal-form.js';
import { createScanCard } from '../components/scan-card.js';

type TodayState = { today: CompanionToday; tasks: CompanionTask[]; prefs: CompanionPrefs };
type Device = { id: string; name: string; status: 'online' | 'offline' | 'revoked' };

export function createHoyView(controller: Pick<SessionController, 'getApi'> & Partial<Pick<SessionController, 'getUser'>>, options: { events?: AccountEvents } = {}): View {
  let active = true;
  let loading = false;
  let refreshQueued = false;
  let state: TodayState | null = null;
  let loadError: string | null = null;
  let devices: Device[] = [];
  let devicesError = false;
  let clock: ReturnType<typeof setInterval> | null = null;
  const expired = new Set<string>();
  const announcedMinutes = new Map<string, number>();
  const lifetime = new AbortController();
  const api = controller.getApi();
  const events = options.events ?? accountEvents;

  const root = h('section', { class: 'view view-hoy', id: 'view-hoy' });
  const greeting = h('h1');
  const dateLine = h('p', { class: 'today-date-line' });
  const portrait = h('span', { class: 'today-face' }); portrait.innerHTML = faceMark();
  const hero = h('header', { class: 'today-hero' }, [
    h('div', { class: 'today-greeting' }, [portrait, h('div', { class: 'today-greeting-copy' }, [dateLine, greeting])]),
  ]);
  const talk = h('a', { href: '#/conversar', class: 'btn primary' }); talk.innerHTML = `${icon('mic')}<span>Hablar</span>`;
  const write = h('a', { href: '#/conversar', class: 'btn ghost' }); write.innerHTML = `${icon('chat')}<span>Escribir</span>`;
  // «Hablar» carries the user's explicit click to Conversar, which opens the microphone; «Escribir» never does.
  write.addEventListener('click', () => { setConversationIntent('text'); });
  talk.addEventListener('click', () => { setConversationIntent('voice'); });
  hero.append(h('div', { class: 'today-hero-actions' }, [talk, write]));
  root.append(hero);
  function greet(): void {
    const now = new Date();
    const zone = state?.prefs.timezone;
    const hour = Number(new Intl.DateTimeFormat('es-ES', { hour: 'numeric', hourCycle: 'h23', ...(zone ? { timeZone: zone } : {}) }).format(now));
    const name = controller.getUser?.()?.displayName?.trim().split(/\s+/)[0];
    greeting.textContent = `${hour < 12 ? 'Buenos días' : hour < 20 ? 'Buenas tardes' : 'Buenas noches'}${name ? `, ${name}` : ''}`;
    dateLine.textContent = new Intl.DateTimeFormat('es-ES', { weekday: 'long', day: 'numeric', month: 'long', ...(zone ? { timeZone: zone } : {}) }).format(now);
  }
  greet();
  const body = h('div', { class: 'section-body today-body' });
  root.appendChild(body);

  const toolbar = h('div', { class: 'today-toolbar' });
  const loadStatus = h('p', { class: 'today-load-status muted', role: 'status', 'aria-live': 'polite' }, ['Cargando lo de hoy…']);
  const refreshButton = h('button', { class: 'btn ghost', type: 'button' }, ['Actualizar']);
  refreshButton.addEventListener('click', () => { void refresh(); });
  toolbar.append(loadStatus, refreshButton);
  body.appendChild(toolbar);

  const grid = h('div', { class: 'today-grid' });
  body.appendChild(grid);

  const habitsPanel = h('section', { class: 'today-panel today-habits-panel', 'aria-labelledby': 'today-habits-title' });
  habitsPanel.append(
    h('h2', { class: 'today-title', id: 'today-habits-title' }, ['Hábitos']),
  );
  const habitForm = h('form', { class: 'today-quick-form' });
  const habitInput = h('input', { class: 'input', maxlength: '80', autocomplete: 'off', required: true, 'aria-label': 'Nombre del nuevo hábito', placeholder: 'Nuevo hábito' });
  const habitSubmit = h('button', { class: 'btn primary', type: 'submit' }, ['Añadir']);
  habitForm.append(h('label', { class: 'field today-grow' }, [h('span', {}, ['Nuevo hábito']), habitInput]), habitSubmit);
  const habitList = h('ul', { class: 'today-list habit-list', 'aria-live': 'polite' });
  const habitReveal = revealForm(habitForm, 'Añadir hábito', 'today-habit-form');
  habitsPanel.prepend(h('div', { class: 'today-panel-head' }, [habitsPanel.querySelector('h2')!, habitReveal.button]));
  habitsPanel.append(habitForm, habitList);

  const tasksPanel = h('section', { class: 'today-panel today-tasks-panel', 'aria-labelledby': 'today-tasks-title' });
  tasksPanel.append(
    h('h2', { class: 'today-title', id: 'today-tasks-title' }, ['Tareas']),
  );
  const taskForm = h('form', { class: 'today-quick-form today-task-form' });
  const taskInput = h('input', { class: 'input', maxlength: '160', autocomplete: 'off', required: true, 'aria-label': 'Título de la nueva tarea', placeholder: 'Nueva tarea' });
  const taskDue = h('input', { class: 'input today-date', type: 'date', 'aria-label': 'Fecha de la tarea' });
  const taskSubmit = h('button', { class: 'btn primary', type: 'submit' }, ['Añadir']);
  taskForm.append(
    h('label', { class: 'field today-grow' }, [h('span', {}, ['Nueva tarea']), taskInput]),
    h('label', { class: 'field' }, [h('span', {}, ['Fecha opcional']), taskDue]),
    taskSubmit,
  );
  const taskList = h('ul', { class: 'today-list task-list', 'aria-live': 'polite' });
  const taskReveal = revealForm(taskForm, 'Añadir tarea', 'today-task-form');
  tasksPanel.prepend(h('div', { class: 'today-panel-head' }, [tasksPanel.querySelector('h2')!, taskReveal.button]));
  tasksPanel.append(taskForm, taskList);

  const timePanel = h('section', { class: 'today-panel today-time-panel', 'aria-labelledby': 'today-time-title' });
  timePanel.append(
    h('h2', { class: 'today-title', id: 'today-time-title' }, ['Temporizador y foco']),
  );
  const timerLabel = h('input', { class: 'input', maxlength: '80', autocomplete: 'off', 'aria-label': 'Etiqueta del temporizador', placeholder: 'Etiqueta opcional' });
  const deviceSelect = h('select', { class: 'select', 'aria-label': 'Aparato para mostrar el temporizador' });
  const deviceField = h('label', { class: 'field today-device' }, [h('span', {}, ['Mostrar también en']), deviceSelect]);
  const timerForm = h('div', { class: 'form-stack today-timer-setup' }, [
    h('label', { class: 'field' }, [h('span', {}, ['Etiqueta']), timerLabel]),
    deviceField,
  ]);
  const presets = h('div', { class: 'today-presets', 'aria-label': 'Duración del temporizador' });
  const presetButtons = [5, 10, 25].map(minutes => {
    const button = h('button', { class: 'btn ghost', type: 'button' }, [`${minutes} min`]);
    button.addEventListener('click', () => { void startTimer(minutes, button); });
    presets.appendChild(button);
    return button;
  });
  const timerState = h('div', { class: 'today-timer-state' });
  const focusState = h('div', { class: 'today-timer-state' });
  const deviceNotice = h('p', { class: 'today-device-notice muted', role: 'status' });
  const countdownLive = h('p', { class: 'visually-hidden', 'aria-live': 'polite', 'aria-atomic': 'true' });
  timerForm.append(presets);
  const timerReveal = revealForm(timerForm, 'Añadir', 'today-timer-form');
  timerReveal.button.setAttribute('aria-label', 'Añadir temporizador');
  timePanel.prepend(h('div', { class: 'today-panel-head' }, [timePanel.querySelector('h2')!, timerReveal.button]));
  timePanel.append(timerForm, timerState, focusState, deviceNotice, countdownLive);

  const remindersPanel = h('section', { class: 'today-panel today-reminders-panel', 'aria-labelledby': 'today-reminders-title' });
  remindersPanel.append(
    h('h2', { class: 'today-title', id: 'today-reminders-title' }, ['Próximos recordatorios']),
  );
  const reminderList = h('ul', { class: 'today-list reminder-list', 'aria-live': 'polite' });
  remindersPanel.appendChild(reminderList);

  const agenda = h('section', { class: 'today-panel today-agenda-panel', 'aria-labelledby': 'today-agenda-title' });
  const addAgenda = h('button', { type: 'button', class: 'btn ghost small' }, ['Añadir']);
  addAgenda.addEventListener('click', () => { taskArchive.open = true; taskReveal.open(); tasksPanel.scrollIntoView?.({ block: 'nearest' }); });
  agenda.append(h('div', { class: 'today-panel-head' }, [h('h2', { id: 'today-agenda-title', class: 'today-title' }, ['Lo próximo de hoy']), addAgenda]));
  const agendaList = h('ul', { class: 'today-list today-agenda-list' }); agenda.append(agendaList);
  const unitPanel = h('section', { class: 'today-panel today-device-panel' }, [h('h2', { class: 'today-title' }, ['Tu ASIS'])]);
  const unitList = h('div', { class: 'today-unit-list' });
  unitPanel.append(unitList, h('a', { href: '#/dispositivos', class: 'btn ghost' }, ['Ver dispositivos']));
  remindersPanel.append(h('a', { href: '#/memoria', class: 'btn ghost small' }, ['Añadir recordatorio']));
  const taskArchive = h('details', { class: 'today-task-archive' }, [h('summary', {}, ['Todas las tareas']), tasksPanel]);
  const scanCard = createScanCard(api);
  grid.append(agenda, timePanel, habitsPanel, unitPanel, scanCard.el, taskArchive, remindersPanel);

  habitForm.addEventListener('submit', async event => {
    event.preventDefault();
    const name = habitInput.value.trim();
    if (!name || habitSubmit.disabled) return;
    habitSubmit.disabled = true;
    try {
      await api.createCompanionHabit(name, lifetime.signal);
      if (!active) return;
      habitInput.value = ''; habitReveal.close(); habitReveal.button.focus();
      toast('Hábito añadido', 'ok');
      await refresh();
    } catch (error) { showActionError('añadir el hábito', error); }
    finally { habitSubmit.disabled = false; }
  });

  taskForm.addEventListener('submit', async event => {
    event.preventDefault();
    const title = taskInput.value.trim();
    if (!title || taskSubmit.disabled) return;
    taskSubmit.disabled = true;
    try {
      await api.createCompanionTask({ title, ...(taskDue.value ? { dueDay: taskDue.value } : {}) }, lifetime.signal);
      if (!active) return;
      taskInput.value = ''; taskDue.value = ''; taskReveal.close(); taskReveal.button.focus();
      toast('Tarea añadida', 'ok');
      await refresh();
    } catch (error) { showActionError('añadir la tarea', error); }
    finally { taskSubmit.disabled = false; }
  });

  function showActionError(action: string, error: unknown): void {
    if (!active || error instanceof RequestAbortError) return;
    toast(`No se pudo ${action}: ${apiMessage(error)}`, 'error');
  }

  async function refresh(): Promise<void> {
    if (!active) return;
    if (loading) { refreshQueued = true; return; }
    loading = true;
    grid.classList.toggle('is-loading', !state);
    refreshButton.disabled = true;
    loadStatus.textContent = state ? 'Actualizando…' : 'Cargando lo de hoy…';
    try {
      const [todayResult, tasksResult, prefsResult, devicesResult] = await Promise.all([
        api.getCompanionToday(lifetime.signal),
        api.listCompanionTasks(lifetime.signal),
        api.getCompanionPrefs(lifetime.signal),
        typeof api.listDevices === 'function' ? api.listDevices(lifetime.signal).then(result => { devicesError = false; return result; }).catch(() => { devicesError = true; return { devices: [] }; }) : Promise.resolve({ devices: [] }),
      ]);
      if (!active) return;
      state = { today: todayResult, tasks: tasksResult.tasks, prefs: prefsResult.prefs };
      devices = devicesResult.devices.filter(device => device.status !== 'revoked');
      loadError = null;
      loadStatus.textContent = '';
      renderAll();
    } catch (error) {
      if (!active || error instanceof RequestAbortError) return;
      loadError = apiMessage(error);
      loadStatus.textContent = `No se pudo cargar Hoy: ${loadError}. Pulsa Actualizar para reintentar.`;
      renderAll();
    } finally {
      loading = false;
      grid.classList.remove('is-loading');
      refreshButton.disabled = false;
      toolbar.hidden = !loadError;
      if (refreshQueued && active) { refreshQueued = false; queueMicrotask(() => { void refresh(); }); }
    }
  }

  function renderAll(): void {
    greet(); renderHabits(); renderTasks(); renderDevices(); renderTimers(); renderReminders(); renderAgenda();
  }

  function fallback(text: string): HTMLLIElement {
    return h('li', { class: loadError ? 'empty today-error' : 'empty' }, [loadError ? 'No hay datos disponibles mientras falla la conexión.' : text]);
  }

  function renderHabits(): void {
    habitList.replaceChildren();
    const habits = state?.today.habits;
    if (!habits) { habitList.appendChild(fallback('Cargando hábitos…')); return; }
    if (!habits.length) { habitList.appendChild(fallback('Aún no hay hábitos. Empieza con algo pequeño: pulsa Añadir hábito.')); return; }
    for (const habit of habits) {
      const button = h('button', { class: 'btn ghost habit-check', type: 'button', 'aria-pressed': String(habit.doneToday), 'aria-label': `${habit.doneToday ? 'Desmarcar' : 'Marcar'} ${habit.name}` }, [habit.doneToday ? 'Desmarcar hoy' : 'Marcar hoy']);
      button.addEventListener('click', async () => {
        button.disabled = true;
        try { await api.setCompanionHabitCheck(habit.id, !habit.doneToday, lifetime.signal); await refresh(); }
        catch (error) { showActionError(habit.doneToday ? 'desmarcar el hábito' : 'marcar el hábito', error); }
        finally { button.disabled = false; }
      });
      const meta = habit.dueToday ? `Racha: ${habit.streak} ${habit.streak === 1 ? 'día' : 'días'}` : `Hoy no toca · Racha: ${habit.streak} ${habit.streak === 1 ? 'día' : 'días'}`;
      habitList.appendChild(h('li', { class: `today-row${habit.doneToday ? ' is-done' : ''}` }, [
        h('div', { class: 'today-row-main' }, [h('span', { class: 'today-row-name' }, [habit.name]), h('span', { class: 'today-row-meta' }, [habit.doneToday ? 'Hecho por hoy' : habit.dueToday ? 'Para hoy' : 'Hoy no toca'])]),
        h('span', { class: 'habit-streak', 'aria-label': meta }, [h('strong', {}, [String(habit.streak)]), h('span', {}, [habit.streak === 1 ? 'día de racha' : 'días de racha'])]), button,
      ]));
    }
  }

  function renderTasks(): void {
    taskList.replaceChildren();
    const tasks = state?.tasks;
    if (!tasks) { taskList.appendChild(fallback('Cargando tareas…')); return; }
    if (!tasks.length) { taskList.appendChild(fallback('No hay tareas. Pulsa Añadir tarea cuando tengas algo pendiente.')); return; }
    for (const task of tasks) {
      const toggle = h('button', { class: 'btn ghost', type: 'button' }, [task.done ? 'Reabrir' : 'Completar']);
      const remove = h('button', { class: 'btn ghost danger', type: 'button', 'aria-label': `Borrar tarea ${task.title}` }, ['Borrar']);
      toggle.addEventListener('click', async () => {
        toggle.disabled = true;
        try { await api.patchCompanionTask(task.id, { done: !task.done }, lifetime.signal); await refresh(); }
        catch (error) { showActionError(task.done ? 'reabrir la tarea' : 'completar la tarea', error); }
        finally { toggle.disabled = false; }
      });
      remove.addEventListener('click', async () => {
        if (!await confirmModal({ title: 'Borrar tarea', body: `Se borrará «${task.title}».`, confirmLabel: 'Borrar', danger: true })) return;
        remove.disabled = true;
        try { await api.deleteCompanionTask(task.id, lifetime.signal); await refresh(); }
        catch (error) { showActionError('borrar la tarea', error); }
        finally { remove.disabled = false; }
      });
      const meta = task.dueDay ? `Fecha: ${formatDay(task.dueDay)}` : 'Sin fecha';
      taskList.appendChild(h('li', { class: `today-row${task.done ? ' is-done' : ''}` }, [
        h('div', { class: 'today-row-main' }, [h('span', { class: 'today-row-name' }, [task.title]), h('span', { class: 'today-row-meta' }, [task.done ? `${meta} · Completada` : meta])]),
        h('div', { class: 'today-row-actions' }, [toggle, remove]),
      ]));
    }
  }

  function renderDevices(): void {
    unitList.replaceChildren();
    if (devicesError || !state) unitList.append(h('p', { class: 'empty' }, [devicesError ? 'No se pudo consultar tu unidad. Pulsa Actualizar para reintentar.' : 'Consultando tu unidad…']));
    else if (!devices.length) unitList.append(h('p', { class: 'empty' }, ['Dale un lugar a ASIS en casa. Añade tu unidad en Dispositivos.']));
    for (const device of devices) {
      const face = h('span', { class: 'today-unit-face' }); face.innerHTML = faceMark();
      unitList.append(h('div', { class: 'today-unit' }, [face, h('div', { class: 'today-row-main' }, [h('strong', {}, [device.name]), h('span', { class: 'today-row-meta' }, [device.status === 'online' ? 'En línea · último estado recibido' : 'Sin conexión · último estado recibido'])])]));
    }
    const previous = deviceSelect.value;
    deviceSelect.replaceChildren(h('option', { value: '' }, ['Solo en esta app']));
    for (const device of devices) deviceSelect.appendChild(h('option', { value: device.id }, [`${device.name} · ${device.status === 'online' ? 'en línea' : 'sin conexión'}`]));
    if (devices.some(device => device.id === previous)) deviceSelect.value = previous;
    else if (devices.length === 1) deviceSelect.value = devices[0]!.id;
    deviceField.hidden = devices.length === 0;
  }

  async function startTimer(minutes: number, button: HTMLButtonElement): Promise<void> {
    if (button.disabled) return;
    button.disabled = true;
    try {
      const response = await api.startCompanionTimer({ seconds: minutes * 60, ...(timerLabel.value.trim() ? { label: timerLabel.value.trim() } : {}), ...(deviceSelect.value ? { deviceId: deviceSelect.value } : {}) }, lifetime.signal);
      if (!active) return;
      timerLabel.value = ''; timerReveal.close(); timerReveal.button.focus();
      showDeviceSupport(response.deviceSupported, response.deviceReason);
      await refresh();
    } catch (error) { showActionError('iniciar el temporizador', error); }
    finally { button.disabled = false; }
  }

  function showDeviceSupport(value: boolean | null, reason?: 'no_device'|'no_capability'|'busy'): void {
    deviceNotice.textContent = value !== false ? '' : reason === 'no_device'
      ? 'El temporizador está guardado, pero el aparato no está conectado.'
      : reason === 'busy' ? 'El temporizador está guardado, pero la cola del aparato está llena. Inténtalo de nuevo en un momento.'
      : 'El temporizador está guardado, pero este aparato necesita una actualización de firmware para mostrarlo.';
  }

  function renderTimers(): void {
    timerState.replaceChildren(); focusState.replaceChildren();
    const timers = state ? (state.today.timers ?? (state.today.timer ? [state.today.timer] : [])) : [];
    const focus = state?.today.focus ?? null;
    for (const button of presetButtons) button.disabled = !state || timers.length >= 5;
    if (!state) {
      timerState.append(h('p', { class: 'empty' }, [loadError ? 'Temporizador no disponible mientras falla la conexión.' : 'Cargando temporizador…']));
      focusState.append(h('p', { class: 'empty' }, [loadError ? 'Foco no disponible mientras falla la conexión.' : 'Cargando foco…']));
      syncClock(); return;
    }
    if (timers.length) for (const timer of timers) timerState.append(timerBlock('Temporizador', timer, async button => {
      button.disabled = true;
      try { await api.stopCompanionTimer(timer.id, lifetime.signal); await refresh(); }
      catch (error) { showActionError('parar el temporizador', error); }
      finally { button.disabled = false; }
    }));
    else timerState.append(h('p', { class: 'empty' }, ['No hay ningún temporizador activo.']));
    if (focus) focusState.append(timerBlock('Foco', focus, async button => {
      button.disabled = true;
      try { await api.stopCompanionFocus(focus.id, lifetime.signal); await refresh(); }
      catch (error) { showActionError('parar el foco', error); }
      finally { button.disabled = false; }
    }));
    else {
      const focusButton = h('button', { class: 'btn ghost', type: 'button' }, [`Iniciar foco · ${state.prefs.focusMinutes} min`]);
      focusButton.addEventListener('click', async () => {
        focusButton.disabled = true;
        try {
          const response = await api.startCompanionFocus({ minutes: state!.prefs.focusMinutes, ...(deviceSelect.value ? { deviceId: deviceSelect.value } : {}) }, lifetime.signal);
          if (!active) return;
          showDeviceSupport(response.deviceSupported, response.deviceReason);
          await refresh();
        } catch (error) { showActionError('iniciar el foco', error); }
        finally { focusButton.disabled = false; }
      });
      focusState.append(h('div', { class: 'today-focus-start' }, [focusButton]));
    }
    updateCountdowns(); syncClock();
  }

  function timerBlock(kind: string, timer: { id: string; label: string; endsAt: number }, stop: (button: HTMLButtonElement) => Promise<void>): HTMLElement {
    const button = h('button', { class: 'btn ghost danger', type: 'button' }, [`Parar ${kind.toLocaleLowerCase('es')}`]);
    button.addEventListener('click', () => { void stop(button); });
    return h('div', { class: 'today-running' }, [
      h('div', { class: 'today-row-main' }, [
        h('span', { class: 'today-row-name' }, [timer.label || kind]),
        countdownRing(timer, kind),
      ]),
      button,
    ]);
  }

  // Today exposes the deadline, not the original duration. The ring is a frame,
  // never a fabricated completion percentage; only the countdown conveys time.
  function countdownRing(timer: { id: string; endsAt: number }, kind: string): HTMLElement {
    const ring = h('div', { class: 'today-clock-ring' });
    ring.innerHTML = '<svg viewBox="0 0 160 160" aria-hidden="true"><circle cx="80" cy="80" r="72" fill="none" stroke="var(--hairline)" stroke-width="5"/></svg>';
    ring.append(h('time', { class: 'today-countdown', role: 'timer', 'aria-live': 'off', dataset: { id: timer.id, kind, endsAt: String(timer.endsAt) } }, ['--:--']));
    return ring;
  }

  function updateCountdowns(): void {
    for (const element of root.querySelectorAll<HTMLElement>('.today-countdown')) {
      const id = element.dataset.id!;
      const kind = element.dataset.kind!;
      const seconds = Math.max(0, Math.ceil((Number(element.dataset.endsAt) - Date.now()) / 1000));
      element.textContent = formatCountdown(seconds);
      element.setAttribute('aria-label', `${kind}: ${formatSpokenTime(seconds)}`);
      const minutes = Math.ceil(seconds / 60);
      if (announcedMinutes.get(id) !== minutes) {
        announcedMinutes.set(id, minutes);
        countdownLive.textContent = seconds === 0 ? `${kind} terminado.` : `${kind}: ${formatSpokenTime(seconds)}.`;
      }
      if (seconds === 0 && !expired.has(id)) { expired.add(id); void refresh(); }
    }
  }

  function syncClock(): void {
    const hasRunning = !!root.querySelector('.today-countdown');
    if (hasRunning && clock === null) clock = setInterval(updateCountdowns, 1000);
    if (!hasRunning && clock !== null) { clearInterval(clock); clock = null; }
  }

  function renderReminders(): void {
    reminderList.replaceChildren();
    const reminders = state?.today.reminders;
    if (!reminders) { reminderList.appendChild(fallback('Cargando recordatorios…')); return; }
    if (!reminders.length) { reminderList.appendChild(fallback('No hay próximos recordatorios.')); return; }
    const formatter = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short', timeZone: state!.prefs.timezone });
    for (const reminder of [...reminders].sort((a, b) => a.dueAt - b.dueAt)) {
      reminderList.appendChild(h('li', { class: 'today-row' }, [
        h('div', { class: 'today-row-main' }, [h('span', { class: 'today-row-name' }, [reminder.title]), h('time', { class: 'today-row-meta', datetime: new Date(reminder.dueAt).toISOString() }, [formatter.format(reminder.dueAt)])]),
      ]));
    }
  }

  function renderAgenda(): void {
    agendaList.replaceChildren();
    if (!state) { agendaList.append(fallback('Preparando tu día…')); return; }
    const { today, tasks, prefs } = state;
    const formatter = new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: prefs.timezone });
    const dayFormatter = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: prefs.timezone });
    const dayOf = (epoch: number) => {
      const parts = dayFormatter.formatToParts(epoch);
      return ['year', 'month', 'day'].map(type => parts.find(part => part.type === type)?.value).join('-');
    };
    const entries: Array<{ title: string; type: string; detail: string; order: number; target: HTMLElement; symbol: string; task?: CompanionTask }> = [];
    for (const task of tasks.filter(item => !item.done && (!item.dueDay || item.dueDay <= today.date))) entries.push({ title: task.title, type: 'Tarea', detail: task.dueDay && task.dueDay < today.date ? 'Pendiente' : task.dueDay ? 'Hoy' : '', order: Number.MAX_SAFE_INTEGER, target: tasksPanel, symbol: 'check', task });
    for (const reminder of today.reminders.filter(item => dayOf(item.dueAt) === today.date)) entries.push({ title: reminder.title, type: 'Recordatorio', detail: formatter.format(reminder.dueAt), order: reminder.dueAt, target: remindersPanel, symbol: 'clock' });
    const timers = today.timers ?? (today.timer ? [today.timer] : []);
    for (const timer of timers) entries.push({ title: timer.label || 'Temporizador', type: 'Temporizador', detail: `Hasta ${formatter.format(timer.endsAt)}`, order: timer.endsAt, target: timePanel, symbol: 'clock' });
    if (today.focus) entries.push({ title: today.focus.label || 'Tiempo para concentrarte', type: 'Foco', detail: `Hasta ${formatter.format(today.focus.endsAt)}`, order: today.focus.endsAt, target: timePanel, symbol: 'sparkle' });
    if (!entries.length) {
      agendaList.append(h('li', { class: 'empty today-agenda-empty' }, [h('strong', {}, ['Tu día tiene espacio.']), h('p', {}, ['Añade una tarea o reserva un rato de foco. ASIS te ayuda a tenerlo a mano.'])]));
      return;
    }
    for (const entry of entries.sort((a, b) => a.order - b.order)) {
      const symbol = h('span', { class: 'today-agenda-icon' }); symbol.innerHTML = icon(entry.symbol);
      const link = h('button', { type: 'button', class: 'today-agenda-link' }, [symbol, h('span', { class: 'today-row-main' }, [h('strong', {}, [entry.title]), ...(entry.task ? [] : [h('span', { class: 'today-row-meta' }, [entry.type])])]), h('span', { class: 'today-agenda-time' }, [entry.detail])]);
      if (entry.task) {
        link.setAttribute('aria-label', `Completar ${entry.title}`);
        link.classList.add('agenda-task');
        link.addEventListener('click', async () => {
          if (link.disabled) return;
          link.disabled = true; link.setAttribute('aria-busy', 'true');
          try {
            await api.patchCompanionTask(entry.task!.id, { done: true }, lifetime.signal);
            if (!active) return;
            link.classList.add('is-complete');
            toast('Tarea completada', 'ok');
            await refresh();
            if (active) addAgenda.focus({ preventScroll: true });
          } catch (error) { showActionError('completar la tarea', error); }
          finally { link.disabled = false; link.removeAttribute('aria-busy'); }
        });
      } else link.addEventListener('click', () => { entry.target.scrollIntoView?.({ block: 'nearest' }); entry.target.querySelector<HTMLElement>('button,a')?.focus(); });
      agendaList.append(h('li', {}, [link]));
    }
  }

  const unsubscribe = events.subscribe(event => {
    if (event.type === 'companion.updated' && event.resource !== 'computer') void refresh();
  });
  void refresh();
  return {
    el: root,
    update() { void refresh(); scanCard.update?.({}); },
    destroy() {
      active = false;
      scanCard.destroy?.();
      lifetime.abort();
      unsubscribe();
      if (clock !== null) { clearInterval(clock); clock = null; }
      habitInput.value = ''; taskInput.value = ''; timerLabel.value = '';
    },
  };
}

function formatCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

function formatSpokenTime(seconds: number): string {
  if (seconds === 0) return 'finalizado';
  const minutes = Math.ceil(seconds / 60);
  return minutes === 1 ? 'queda 1 minuto' : `quedan ${minutes} minutos`;
}

function formatDay(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(Date.UTC(year, month - 1, day)));
}

function apiMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : 'error inesperado';
}
